import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import '../styles/dashboard.css';
import '../styles/students.css';
import '../styles/counselors.css';
import '../styles/applications.css';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { ViewModeWrap } from '../components/ViewRecordModal';
import StudentSearchSelect from '../components/StudentSearchSelect';
import CountrySearchSelect from '../components/CountrySearchSelect';
import DocumentFileActions from '../components/DocumentFileActions';
import ApplicationReferenceDetails, { mapReferencesFromApi } from '../components/ApplicationReferenceDetails';
import { sortCountriesByPriority } from '../constants/countryPriority';
import { useAuth } from '../context/AuthContext';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileAlt, faSave, faTimes, faUpload, faTrash, faArrowLeft, faPassport, faPrint } from "@fortawesome/free-solid-svg-icons";
import {
  APPLICATION_CHANNELS,
  APPLICATION_DOCUMENT_TYPES,
  APPLICATION_STATUS_OPTIONS,
  OFFER_LETTER_OPTIONS,
  PAID_STATUS_OPTIONS,
  TAGGING_STATUS_OPTIONS,
  TUITION_STATUS_OPTIONS,
  getIntakeOptions,
  normalizeTaggingStatus,
} from '../constants/applicationOptions';

const API_URL = process.env.REACT_APP_API_URL || 'https://unilinkapi-production.up.railway.app';

const parseStoredOption = (value, options) => {
  if (!value) return { selected: '', other: '' };
  if (options.includes(value)) return { selected: value, other: '' };
  if (String(value).startsWith('Other:')) return { selected: 'Other', other: String(value).replace(/^Other:\s*/, '') };
  return { selected: 'Other', other: value };
};

const parseIntake = (app) => {
  const value = app.intake || '';
  if (!value && app.intake_name) return parseStoredOption(app.intake_name, getIntakeOptions());
  return parseStoredOption(value, getIntakeOptions());
};

export default function EditApplication({ recordId, viewOnly = false } = {}) {
  const navigate = useNavigate();
  const { id: paramId } = useParams();
  const id = recordId || paramId;
  const { user } = useAuth();
  const isStudentPortal = user?.role === 'client';
  const isViewOnly = viewOnly || isStudentPortal;
  const [loading, setLoading] = useState(false);
  const [creatingVisa, setCreatingVisa] = useState(false);
  const [showVisaModal, setShowVisaModal] = useState(false);
  const [visaModalError, setVisaModalError] = useState('');
  const [createdVisa, setCreatedVisa] = useState(null);
  const [fetchingData, setFetchingData] = useState(true);
  const [applicationMeta, setApplicationMeta] = useState({
    studentName: '',
    studentEmail: '',
    studentPhone: '',
    countryName: '',
    universityName: '',
    applicationId: '',
  });
  const [formData, setFormData] = useState({
    applicationDate: '',
    studentId: '',
    countryId: '',
    universityId: '',
    program: '',
    intake: '',
    intakeOther: '',
    appsSubmittedThrough: '',
    appsSubmittedThroughOther: '',
    appsTaggedThrough: '',
    appsTaggedThroughOther: '',
    taggingStatus: 'Not Tagged',
    applicationStatus: 'Pending',
    offerLetterType: '',
    offerLetterOther: '',
    universityFees: '',
    initialDepositStatus: '',
    initialDepositAmount: '',
    enrollmentFeeStatus: '',
    enrollmentFeeAmount: '',
    tuitionFeeStatus: '',
    tuitionFeeAmount: '',
    condition: '',
    remarks: '',
  });
  const [references, setReferences] = useState([]);

  const [students, setStudents] = useState([]);
  const [countries, setCountries] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(true);
  const [uploadingType, setUploadingType] = useState('');
  const [offerLetterFile, setOfferLetterFile] = useState(null);
  const [offerLetterLoading, setOfferLetterLoading] = useState(false);
  const [offerLetterUploading, setOfferLetterUploading] = useState(false);
  const intakeOptions = getIntakeOptions();

  useEffect(() => {
    fetchStudents();
    fetchCountries();
    fetchApplicationData();
    fetchOfferLetter();
  }, [id]);

  useEffect(() => {
    if (formData.countryId) fetchUniversitiesByCountry(formData.countryId);
  }, [formData.countryId]);

  const fetchDocuments = async ({ showLoading = false } = {}) => {
    if (showLoading) setLoadingDocuments(true);
    try {
      const res = await fetch(`${API_URL}/applications/${id}/documents`);
      const data = await res.json();
      if (data.success) setDocuments(data.documents || []);
      else setDocuments([]);
    } catch (e) {
      console.error(e);
      setDocuments([]);
    } finally {
      if (showLoading) setLoadingDocuments(false);
    }
  };

  const fetchOfferLetter = async () => {
    setOfferLetterLoading(true);
    try {
      const res = await fetch(`${API_URL}/applications/${id}/offer-letter`);
      const data = await res.json();
      if (data.success) setOfferLetterFile(data.offerLetter || null);
      else setOfferLetterFile(null);
    } catch (e) {
      console.error(e);
      setOfferLetterFile(null);
    } finally {
      setOfferLetterLoading(false);
    }
  };

  const handleOfferLetterUpload = async (file) => {
    if (isViewOnly || !file) return;
    setOfferLetterUploading(true);
    try {
      const fd = new FormData();
      fd.append('offerLetter', file);
      const res = await fetch(`${API_URL}/applications/${id}/offer-letter`, { method: 'POST', body: fd });
      const data = await res.json();
      if (data.success) {
        await fetchOfferLetter();
        alert('Offer letter uploaded successfully');
      } else {
        alert(data.message || 'Failed to upload offer letter');
      }
    } catch (e) {
      alert('Network error while uploading offer letter');
    } finally {
      setOfferLetterUploading(false);
    }
  };

  const handleOfferLetterDelete = async () => {
    if (isViewOnly) return;
    if (!window.confirm('Delete the uploaded offer letter?')) return;
    try {
      const res = await fetch(`${API_URL}/applications/${id}/offer-letter`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        setOfferLetterFile(null);
        alert('Offer letter deleted');
      } else {
        alert(data.message || 'Failed to delete offer letter');
      }
    } catch (e) {
      alert('Network error while deleting offer letter');
    }
  };

  const fetchApplicationData = async () => {
    try {
      setFetchingData(true);
      setLoadingDocuments(true);
      const [appRes, docsRes] = await Promise.all([
        fetch(`${API_URL}/applications/${id}`),
        fetch(`${API_URL}/applications/${id}/documents`),
      ]);
      const data = await appRes.json();
      const docsData = await docsRes.json();

      if (docsData.success) setDocuments(docsData.documents || []);
      else setDocuments([]);

      if (data.success) {
        const app = data.application;
        if (isStudentPortal && String(app.student_id) !== String(user?.id)) {
          alert('You can only edit your own applications.');
          navigate('/my-applications', { replace: true });
          return;
        }
        const intake = parseIntake(app);
        const submitted = parseStoredOption(app.apps_submitted_through, APPLICATION_CHANNELS);
        const tagged = parseStoredOption(app.apps_tagged_through, APPLICATION_CHANNELS);
        const offer = parseStoredOption(app.offer_letter_type, OFFER_LETTER_OPTIONS);
        setFormData({
          applicationDate: app.application_date ? String(app.application_date).split('T')[0] : '',
          studentId: app.student_id,
          countryId: app.country_id,
          universityId: app.university_id,
          program: app.program || '',
          intake: intake.selected,
          intakeOther: intake.other || app.intake_other || '',
          appsSubmittedThrough: submitted.selected,
          appsSubmittedThroughOther: app.apps_submitted_through_other || submitted.other,
          appsTaggedThrough: tagged.selected,
          appsTaggedThroughOther: app.apps_tagged_through_other || tagged.other,
          taggingStatus: normalizeTaggingStatus(app.tagging_status),
          applicationStatus: app.application_status || 'Pending',
          offerLetterType: offer.selected,
          offerLetterOther: app.offer_letter_other || offer.other,
          universityFees: app.university_fees || '',
          initialDepositStatus: app.initial_deposit_status || '',
          initialDepositAmount: app.initial_deposit_amount || '',
          enrollmentFeeStatus: app.enrollment_fee_status || '',
          enrollmentFeeAmount: app.enrollment_fee_amount || '',
          tuitionFeeStatus: app.tuition_fee_status || '',
          tuitionFeeAmount: app.tuition_fee_amount || '',
          condition: app.condition || '',
          remarks: app.remarks || '',
        });
        setReferences(mapReferencesFromApi(app.references));
        setApplicationMeta({
          studentName: app.student_full_name || app.student_name || '',
          studentEmail: app.student_email || '',
          studentPhone: app.student_mobile || '',
          countryName: app.country_name || '',
          universityName: app.university_name || '',
          applicationId: app.application_id || '',
        });
      } else {
        alert('Application not found');
        navigate(isStudentPortal ? '/my-applications' : '/applications');
      }
    } catch (error) {
      alert('Failed to load application data');
      setDocuments([]);
    } finally {
      setFetchingData(false);
      setLoadingDocuments(false);
    }
  };

  const fetchStudents = async () => {
    try {
      const res = await fetch(`${API_URL}/students`);
      const data = await res.json();
      if (data.success) setStudents(data.students);
    } catch (e) { console.error(e); }
  };

  const fetchCountries = async () => {
    try {
      const res = await fetch(`${API_URL}/countries`);
      const data = await res.json();
      if (data.success) setCountries(sortCountriesByPriority(data.countries || []));
    } catch (e) { console.error(e); }
  };

  const fetchUniversitiesByCountry = async (countryId) => {
    try {
      const res = await fetch(`${API_URL}/universities/by-country/${countryId}`);
      const data = await res.json();
      if (data.success) setUniversities(data.universities);
    } catch (e) { setUniversities([]); }
  };

  const handleChange = (e) => {
    if (isViewOnly) return;
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const openVisaModal = () => {
    setVisaModalError('');
    setCreatedVisa(null);
    setShowVisaModal(true);
  };

  const closeVisaModal = () => {
    if (creatingVisa) return;
    setShowVisaModal(false);
    setVisaModalError('');
    setCreatedVisa(null);
  };

  const handleAssignToVisa = async () => {
    setCreatingVisa(true);
    setVisaModalError('');
    try {
      const res = await fetch(`${API_URL}/applications/${id}/create-visa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (data.success) {
        setCreatedVisa({
          visaId: data.visaId,
          generatedVisaId: data.generatedVisaId,
          transferredDocuments: data.transferredDocuments || 0,
          transferred: data.transferred || [],
        });
      } else {
        setVisaModalError(data.message || 'Failed to create visa');
      }
    } catch (err) {
      setVisaModalError('Network error. Please try again.');
    } finally {
      setCreatingVisa(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isViewOnly) return;
    if (!formData.studentId || !formData.countryId || !formData.universityId || !formData.program) {
      alert('Please fill in all required fields');
      return;
    }
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/applications/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, references })
      });
      const data = await response.json();
      if (data.success) { alert('Application updated successfully!'); navigate(isStudentPortal ? '/my-applications' : '/applications'); }
      else alert(data.message || 'Failed to update application');
    } catch (error) {
      alert('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentUpload = async (documentType, file) => {
    if (!file) return;
    setUploadingType(documentType);
    try {
      const fd = new FormData();
      fd.append('document', file);
      fd.append('documentType', documentType);
      const res = await fetch(`${API_URL}/applications/${id}/documents`, { method: 'POST', body: fd });
      const data = await res.json();
      if (data.success) fetchDocuments();
      else alert(data.message || 'Failed to upload document');
    } catch (e) {
      alert('Network error. Please try again.');
    } finally {
      setUploadingType('');
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      const res = await fetch(`${API_URL}/applications/${id}/documents/${docId}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) fetchDocuments();
      else alert(data.message || 'Failed to delete');
    } catch (e) { alert('Network error.'); }
  };

  const docsForType = (type) => documents.filter(d => d.document_type === type);
  const showOfferLetter = formData.applicationStatus === 'Accepted';

  if (fetchingData) {
    return (
      <ViewModeWrap viewOnly={viewOnly}>
        <Sidebar />
        <div className="main-content" id="mainContent">
          <Header />
          <div style={{ textAlign: 'center', padding: '40px' }}><p>Loading application data...</p></div>
        </div>
      </ViewModeWrap>
    );
  }

  return (
    <ViewModeWrap viewOnly={viewOnly}>
      <Sidebar />
      <div className="main-content" id="mainContent">
        <Header />
        <div className="students-container">
          <div className="students-header no-print">
            <div className="header-left">
              <h1>
                <FontAwesomeIcon icon={faFileAlt} className="page-icon" />
                {isViewOnly ? 'Application Details' : 'Edit Application'}
              </h1>
            </div>
            <div className="header-right">
              <button type="button" className="btn-secondary" onClick={() => window.print()} style={{ marginRight: '10px' }}>
                <FontAwesomeIcon icon={faPrint} /> Print
              </button>
              {!isStudentPortal && !isViewOnly && (
                <button
                  type="button"
                  className="btn-primary"
                  onClick={openVisaModal}
                  style={{ marginRight: '10px' }}
                >
                  <FontAwesomeIcon icon={faPassport} style={{ marginRight: '8px' }} />
                  Assign To Visa
                </button>
              )}
              <Link
                to={isStudentPortal ? '/my-applications' : '/applications'}
                className="btn-secondary"
              >
                <FontAwesomeIcon icon={faArrowLeft} /> Back to Applications
              </Link>
            </div>
          </div>

          {applicationMeta.applicationId && (
            <div className="student-id-card">
              <div className="row align-items-center">
                <div className="col-md-12">
                  <span className="id-label">Application ID:</span>
                  <span className="student-id-display">{applicationMeta.applicationId}</span>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className={`counselor-form student-form ${isViewOnly ? 'form-view-only' : ''}`}>
            <div className="form-section">
              <h2 className="section-title">Application Management</h2>
              <div className="row">
                <div className="col-md-4 mb-3">
                  <label className="form-label">Application Date <span className="required">*</span></label>
                  <input type="date" className="input-field" name="applicationDate" value={formData.applicationDate} onChange={handleChange} required disabled={isViewOnly} />
                </div>
                <div className="col-md-8 mb-3">
                  <label className="form-label">Student <span className="required">*</span></label>
                  <StudentSearchSelect
                    students={students}
                    value={formData.studentId}
                    onChange={(studentId) => setFormData(prev => ({ ...prev, studentId }))}
                    required
                    disabled={isViewOnly}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Country <span className="required">*</span></label>
                  <CountrySearchSelect
                    countries={countries}
                    value={formData.countryId}
                    onChange={(countryId) => setFormData(prev => ({ ...prev, countryId }))}
                    required
                    disabled={isViewOnly}
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">University <span className="required">*</span></label>
                  <select className="input-field" name="universityId" value={formData.universityId} onChange={handleChange} required disabled={!formData.countryId}>
                    <option value="">Select University</option>
                    {universities.map(u => <option key={u.id} value={u.id}>{u.university_name}</option>)}
                  </select>
                </div>
              </div>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Program <span className="required">*</span></label>
                  <input type="text" className="input-field" name="program" value={formData.program} onChange={handleChange} required />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Intake</label>
                  <select className="input-field" name="intake" value={formData.intake} onChange={handleChange}>
                    <option value="">Select Intake</option>
                    {intakeOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    {formData.intake && !intakeOptions.includes(formData.intake) && (
                      <option value={formData.intake}>{formData.intake}</option>
                    )}
                  </select>
                </div>
                {formData.intake === 'Other' && (
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Other Intake</label>
                    <input type="text" className="input-field" name="intakeOther" value={formData.intakeOther} onChange={handleChange} />
                  </div>
                )}
              </div>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Application Submitted Through</label>
                  <select className="input-field" name="appsSubmittedThrough" value={formData.appsSubmittedThrough} onChange={handleChange}>
                    <option value="">Select</option>
                    {APPLICATION_CHANNELS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </div>
                {formData.appsSubmittedThrough === 'Other' && (
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Other Submitted Through</label>
                    <input type="text" className="input-field" name="appsSubmittedThroughOther" value={formData.appsSubmittedThroughOther} onChange={handleChange} />
                  </div>
                )}
                <div className="col-md-6 mb-3">
                  <label className="form-label">Application Tagged Through</label>
                  <select className="input-field" name="appsTaggedThrough" value={formData.appsTaggedThrough} onChange={handleChange}>
                    <option value="">Select</option>
                    {APPLICATION_CHANNELS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </div>
                {formData.appsTaggedThrough === 'Other' && (
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Other Tagged Through</label>
                    <input type="text" className="input-field" name="appsTaggedThroughOther" value={formData.appsTaggedThroughOther} onChange={handleChange} />
                  </div>
                )}
              </div>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Tagging Status</label>
                  <select className="input-field" name="taggingStatus" value={formData.taggingStatus} onChange={handleChange}>
                    {TAGGING_STATUS_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Application Status</label>
                  <select className="input-field" name="applicationStatus" value={formData.applicationStatus} onChange={handleChange}>
                    {APPLICATION_STATUS_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    {formData.applicationStatus && !APPLICATION_STATUS_OPTIONS.includes(formData.applicationStatus) && (
                      <option value={formData.applicationStatus}>{formData.applicationStatus}</option>
                    )}
                  </select>
                </div>
              </div>
              {showOfferLetter && (
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Offer Letter</label>
                    <select className="input-field" name="offerLetterType" value={formData.offerLetterType} onChange={handleChange}>
                      <option value="">Select</option>
                      {OFFER_LETTER_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </div>
                  {formData.offerLetterType === 'Other' && (
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Other Offer Letter</label>
                      <input type="text" className="input-field" name="offerLetterOther" value={formData.offerLetterOther} onChange={handleChange} />
                    </div>
                  )}
                </div>
              )}
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">University Fees</label>
                  <input type="text" className="input-field" name="universityFees" value={formData.universityFees} onChange={handleChange} placeholder="e.g. £2,000 or $1,500" />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Initial Deposit</label>
                  <select className="input-field" name="initialDepositStatus" value={formData.initialDepositStatus} onChange={handleChange}>
                    <option value="">Select</option>
                    {PAID_STATUS_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </div>
                {formData.initialDepositStatus === 'Paid' && (
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Initial Deposit Amount</label>
                    <input type="text" className="input-field" name="initialDepositAmount" value={formData.initialDepositAmount} onChange={handleChange} />
                  </div>
                )}
              </div>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Enrollment Fee</label>
                  <select className="input-field" name="enrollmentFeeStatus" value={formData.enrollmentFeeStatus} onChange={handleChange}>
                    <option value="">Select</option>
                    {PAID_STATUS_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </div>
                {formData.enrollmentFeeStatus === 'Paid' && (
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Enrollment Fee Amount</label>
                    <input type="text" className="input-field" name="enrollmentFeeAmount" value={formData.enrollmentFeeAmount} onChange={handleChange} />
                  </div>
                )}
                <div className="col-md-6 mb-3">
                  <label className="form-label">Tuition Fee</label>
                  <select className="input-field" name="tuitionFeeStatus" value={formData.tuitionFeeStatus} onChange={handleChange}>
                    <option value="">Select</option>
                    {TUITION_STATUS_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </div>
                {formData.tuitionFeeStatus === 'Paid' && (
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Tuition Fee Amount</label>
                    <input type="text" className="input-field" name="tuitionFeeAmount" value={formData.tuitionFeeAmount} onChange={handleChange} />
                  </div>
                )}
              </div>
              <div className="row">
                <div className="col-md-12 mb-3">
                  <label className="form-label">Conditions</label>
                  <textarea className="input-field" name="condition" value={formData.condition} onChange={handleChange} rows="3" />
                </div>
              </div>
              <div className="row">
                <div className="col-md-12 mb-3">
                  <label className="form-label">Remarks</label>
                  <textarea className="input-field" name="remarks" value={formData.remarks} onChange={handleChange} rows="3" />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h2 className="section-title">Offer Letter File</h2>
              <p style={{ color: '#6b7280', fontSize: '13px', marginBottom: '14px' }}>
                {isStudentPortal
                  ? 'Download the offer letter uploaded for this application (when available).'
                  : 'Upload the official offer letter file for this application. Students can download it from their portal.'}
              </p>
              {offerLetterLoading ? (
                <div style={{ padding: '16px', color: '#6b7280', fontSize: '13px' }}>Loading offer letter…</div>
              ) : offerLetterFile ? (
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap',
                  padding: '14px 16px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '10px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
                    <FontAwesomeIcon icon={faFileAlt} style={{ color: '#166534', fontSize: '22px' }} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 700, color: '#166534', fontSize: '14px' }}>Offer letter available</div>
                      <div style={{ fontSize: '12px', color: '#4b5563' }}>
                        Uploaded{offerLetterFile.uploadedAt ? ` on ${new Date(offerLetterFile.uploadedAt).toLocaleString()}` : ''}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <DocumentFileActions
                      fileUrl={`${API_URL}${offerLetterFile.filePath}`}
                      fileName={offerLetterFile.filePath?.split('/').pop() || 'offer-letter'}
                      showLabels
                    />
                    {!isViewOnly && (
                      <button type="button" className="btn-icon btn-delete" title="Delete offer letter" onClick={handleOfferLetterDelete}>
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div style={{
                  padding: '16px', background: '#f9fafb', border: '1px dashed #d1d5db',
                  borderRadius: '10px', color: '#6b7280', fontSize: '13px'
                }}>
                  {isViewOnly
                    ? 'No offer letter has been uploaded for this application yet.'
                    : 'No offer letter uploaded yet.'}
                </div>
              )}
              {!isViewOnly && (
                <div style={{ marginTop: '12px' }}>
                  <label className="btn-secondary btn-sm" style={{ cursor: offerLetterUploading ? 'wait' : 'pointer', margin: 0 }}>
                    <FontAwesomeIcon icon={faUpload} /> {offerLetterUploading ? 'Uploading...' : (offerLetterFile ? 'Replace Offer Letter' : 'Upload Offer Letter')}
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      style={{ display: 'none' }}
                      disabled={offerLetterUploading}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        e.target.value = '';
                        if (file) handleOfferLetterUpload(file);
                      }}
                    />
                  </label>
                </div>
              )}
            </div>

            <ApplicationReferenceDetails
              references={references}
              onChange={setReferences}
              viewOnly={isViewOnly}
            />

            <div className="form-section no-print">
              <h2 className="section-title">Documents Submission</h2>
              {loadingDocuments ? (
                <div style={{
                  padding: '28px 20px', textAlign: 'center', color: '#6b7280',
                  background: '#f9fafb', border: '1px dashed #d1d5db', borderRadius: '12px'
                }}>
                  <p style={{ margin: 0, fontSize: '14px', fontWeight: 500 }}>Loading documents...</p>
                  <p style={{ margin: '6px 0 0', fontSize: '12px' }}>Please wait while uploaded files are fetched.</p>
                </div>
              ) : (
                <div className="row g-3">
                  {APPLICATION_DOCUMENT_TYPES.map((type) => {
                    const files = docsForType(type);
                    return (
                      <div key={type} className="col-md-6">
                        <div style={{
                          border: files.length ? '2px solid #22c55e' : '2px dashed #d1d5db',
                          borderRadius: '12px', padding: '16px', background: files.length ? '#f0fdf4' : '#fafafa'
                        }}>
                          <div style={{ fontWeight: 600, marginBottom: '8px' }}>{type}</div>
                          {files.map((doc) => (
                            <div key={doc.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '8px' }}>
                              <span style={{ fontSize: '12px', color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {doc.original_name || 'Uploaded file'}
                              </span>
                              <div style={{ display: 'flex', gap: '6px', flexShrink: 0, alignItems: 'center' }}>
                                <DocumentFileActions
                                  fileUrl={`${API_URL}${doc.file_path}`}
                                  fileName={doc.original_name || 'document'}
                                />
                                {!isViewOnly && (
                                  <button type="button" className="btn-icon btn-delete" onClick={() => handleDeleteDocument(doc.id)}>
                                    <FontAwesomeIcon icon={faTrash} />
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                          {!isViewOnly && (
                            <label className="btn-secondary btn-sm" style={{ cursor: 'pointer', margin: 0 }}>
                              <FontAwesomeIcon icon={faUpload} /> {uploadingType === type ? 'Uploading...' : 'Upload'}
                              <input
                                type="file"
                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                style={{ display: 'none' }}
                                disabled={uploadingType === type}
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  e.target.value = '';
                                  if (file) handleDocumentUpload(type, file);
                                }}
                              />
                            </label>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {!isViewOnly && (
              <div className="form-actions no-print">
                <button type="button" className="btn-secondary" onClick={() => navigate(isStudentPortal ? '/my-applications' : '/applications')} disabled={loading}>
                  <FontAwesomeIcon icon={faTimes} /> Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={loading}>
                  <FontAwesomeIcon icon={faSave} /> {loading ? 'Updating...' : 'Update Application'}
                </button>
              </div>
            )}
          </form>
        </div>

        {showVisaModal && (
          <>
            <div className="modal-backdrop show" onClick={closeVisaModal}></div>
            <div className="modal show" style={{ display: 'block' }} tabIndex="-1">
              <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                  <div className="modal-header" style={{
                    background: 'linear-gradient(135deg, #F47B20 0%, #e8660f 100%)',
                    padding: '20px 28px', borderBottom: 'none'
                  }}>
                    <div>
                      <h5 className="modal-title" style={{ color: 'white', fontSize: '18px', fontWeight: '700', margin: 0 }}>
                        <FontAwesomeIcon icon={faPassport} style={{ marginRight: '10px' }} />
                        {createdVisa ? 'Visa Created' : 'Assign To Visa'}
                      </h5>
                      <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '13px', margin: '4px 0 0' }}>
                        {createdVisa
                          ? 'Visa record is ready to edit'
                          : 'Create a visa from this application'}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="btn-close-modal"
                      onClick={closeVisaModal}
                      disabled={creatingVisa}
                      style={{
                        color: 'white', background: 'rgba(255,255,255,0.2)', border: 'none',
                        borderRadius: '8px', width: '36px', height: '36px', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                      }}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>

                  <div className="modal-body" style={{ padding: '24px 28px' }}>
                    {visaModalError && (
                      <div style={{
                        background: '#fee2e2', color: '#991b1b', padding: '12px 16px',
                        borderRadius: '10px', marginBottom: '16px', border: '1px solid #fecaca',
                        fontSize: '13px'
                      }}>
                        {visaModalError}
                      </div>
                    )}

                    {createdVisa ? (
                      <div style={{ textAlign: 'center', padding: '8px 0 4px' }}>
                        <div style={{
                          width: '64px', height: '64px', borderRadius: '50%', margin: '0 auto 16px',
                          background: '#dcfce7', color: '#16a34a', display: 'flex',
                          alignItems: 'center', justifyContent: 'center', fontSize: '28px'
                        }}>
                          <FontAwesomeIcon icon={faPassport} />
                        </div>
                        <p style={{ fontSize: '15px', color: '#374151', marginBottom: '8px', lineHeight: 1.6 }}>
                          Visa <strong>{createdVisa.generatedVisaId}</strong> has been created successfully.
                        </p>
                        <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 12px' }}>
                          Student, country, and university details were copied from this application.
                        </p>
                        {createdVisa.transferredDocuments > 0 ? (
                          <div style={{
                            textAlign: 'left', background: '#f0fdf4', border: '1px solid #bbf7d0',
                            borderRadius: '10px', padding: '12px 14px', fontSize: '13px', color: '#166534'
                          }}>
                            <strong>{createdVisa.transferredDocuments}</strong> document(s) transferred:
                            <ul style={{ margin: '8px 0 0', paddingLeft: '18px' }}>
                              {(createdVisa.transferred || []).map((t, i) => (
                                <li key={i}>{t.from} → {t.to}{t.original_name ? ` (${t.original_name})` : ''}</li>
                              ))}
                            </ul>
                          </div>
                        ) : (
                          <p style={{ fontSize: '13px', color: '#9a3412', margin: 0 }}>
                            No Passport or Educational Documents were found on this application to transfer.
                          </p>
                        )}
                      </div>
                    ) : (
                      <>
                        <p style={{ fontSize: '15px', color: '#374151', marginBottom: '16px', lineHeight: 1.6 }}>
                          Create a new visa for <strong>{applicationMeta.studentName || 'this student'}</strong>?
                          The fields below will be filled automatically from the application.
                        </p>
                        <div style={{
                          background: '#f9fafb', padding: '16px', borderRadius: '10px',
                          border: '1px solid #e5e7eb', fontSize: '13px', color: '#374151'
                        }}>
                          {applicationMeta.applicationId && (
                            <div style={{ marginBottom: '8px' }}><strong>Application:</strong> {applicationMeta.applicationId}</div>
                          )}
                          <div style={{ marginBottom: '8px' }}><strong>Student:</strong> {applicationMeta.studentName || '-'}</div>
                          <div style={{ marginBottom: '8px' }}><strong>Email:</strong> {applicationMeta.studentEmail || '-'}</div>
                          <div style={{ marginBottom: '8px' }}><strong>Phone:</strong> {applicationMeta.studentPhone || '-'}</div>
                          <div style={{ marginBottom: '8px' }}><strong>Country:</strong> {applicationMeta.countryName || '-'}</div>
                          <div><strong>University:</strong> {applicationMeta.universityName || '-'}</div>
                        </div>
                        <div style={{
                          marginTop: '16px', background: '#fff7ed', padding: '14px 16px',
                          borderRadius: '10px', border: '1px solid #fed7aa', fontSize: '13px', color: '#9a3412'
                        }}>
                          <FontAwesomeIcon icon={faPassport} style={{ marginRight: '8px', color: '#F47B20' }} />
                          Visa type defaults to <strong>Study Visa</strong> / <strong>In Progress</strong>.
                          Passport and Educational Documents will be copied onto the visa.
                        </div>
                      </>
                    )}
                  </div>

                  <div className="modal-footer" style={{
                    padding: '16px 28px', borderTop: '1px solid #f3f4f6',
                    display: 'flex', justifyContent: 'flex-end', gap: '10px'
                  }}>
                    {createdVisa ? (
                      <>
                        <button type="button" className="btn-secondary" onClick={closeVisaModal}>
                          Close
                        </button>
                        <button
                          type="button"
                          className="btn-primary"
                          onClick={() => navigate(`/edit-visa/${createdVisa.visaId}`)}
                        >
                          <FontAwesomeIcon icon={faPassport} style={{ marginRight: '8px' }} />
                          Open Visa
                        </button>
                      </>
                    ) : (
                      <>
                        <button type="button" className="btn-secondary" onClick={closeVisaModal} disabled={creatingVisa}>
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="btn-primary"
                          onClick={handleAssignToVisa}
                          disabled={creatingVisa}
                        >
                          <FontAwesomeIcon icon={faPassport} style={{ marginRight: '8px' }} />
                          {creatingVisa ? 'Creating...' : 'Confirm & Create Visa'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </ViewModeWrap>
  );
}
